// Single entry point for "how many bytes is this video" — the size counterpart
// to videoDuration.js, and there for the same reason: the class-recording card
// beside a completed Broadcast class has no admin-library row to read a size off
// (see ScheduleDetailsDrawer.js), and the VOD record's own `fileSize` comes back
// 0, so the card was stuck showing "0 KB" for recordings that really are 102 KB,
// 40 MB, whatever. The only honest number comes from measuring the media itself.
//
// How it measures a single-file URL (an MP4, or the VOD's download URL — the same
// one file the Download action streams):
//  1. A HEAD request, whose content-length is the exact answer for one request's
//     worth of traffic. content-length is CORS-safelisted, so it's readable
//     without the server exposing anything extra.
//  2. If HEAD isn't allowed (plenty of media servers only answer GET), a GET that
//     is aborted the moment its headers land. fetch() resolves as soon as the
//     response head arrives, so content-length is readable there before any of
//     the body is pulled down — this is the same header the Download action reads
//     off its own GET to drive the progress bar, so it's known to work here.
//
// An HLS recording isn't one file, so its size is the sum of its segments.
// Segment counts scale with class length (an hour-long class is hundreds of
// them), so past SEGMENT_SAMPLE_LIMIT we measure an evenly spread sample and
// scale it by duration rather than firing one request per segment.
//
// Every failure path resolves to null rather than throwing, so a caller that
// can't get a number just keeps showing whatever it had.

import { isHlsUrl, resolveHlsMediaPlaylist } from './videoDuration';

// Enough segments to average out bitrate variation without a request storm.
const SEGMENT_SAMPLE_LIMIT = 40;
// Keeps a segment sample from monopolising the browser's connection pool while
// the rest of the drawer is still loading.
const MAX_CONCURRENT_REQUESTS = 6;

const readContentLength = (response) => {
  const bytes = Number(response.headers.get('content-length'));
  return Number.isFinite(bytes) && bytes > 0 ? bytes : null;
};

const headContentLength = async (url) => {
  try {
    const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
    if (!response.ok) return null;
    return readContentLength(response);
  } catch (e) {
    // Network error, or a CORS preflight refusing HEAD — try the GET path.
    return null;
  }
};

const getContentLengthWithoutBody = async (url) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  try {
    const response = await fetch(url, {
      cache: 'no-store',
      ...(controller ? { signal: controller.signal } : {}),
    });
    const bytes = response.ok ? readContentLength(response) : null;
    // Headers are in hand, so drop the connection instead of downloading the
    // whole recording just to learn how big it is.
    try {
      if (controller) controller.abort();
      else if (response.body && response.body.cancel) response.body.cancel();
    } catch (e) { /* already closed - nothing to release */ }
    return bytes;
  } catch (e) {
    return null;
  }
};

// Exact byte count for a URL that points at one file, or null.
const measureFile = async (url) => {
  const fromHead = await headContentLength(url);
  if (fromHead) return fromHead;
  return getContentLengthWithoutBody(url);
};

const mapWithConcurrency = async (items, worker, limit) => {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await worker(items[index]);
    }
  });
  await Promise.all(runners);
  return results;
};

const getHlsSize = async (url) => {
  const { segments, durationSec } = await resolveHlsMediaPlaylist(url);
  if (!segments.length) return null;

  // Take every Nth segment so the sample spans the whole recording — measuring
  // only the first 40 would read the encoder's warm-up as the whole bitrate.
  const step = Math.max(1, Math.ceil(segments.length / SEGMENT_SAMPLE_LIMIT));
  const sampled = segments.filter((_, index) => index % step === 0);

  const sizes = await mapWithConcurrency(sampled, (segment) => measureFile(segment.url), MAX_CONCURRENT_REQUESTS);
  const measured = sampled
    .map((segment, index) => ({ segment, bytes: sizes[index] }))
    .filter(({ bytes }) => bytes !== null);
  if (!measured.length) return null;

  const measuredBytes = measured.reduce((sum, item) => sum + item.bytes, 0);
  if (measured.length === segments.length) {
    return { bytes: measuredBytes, isExact: true, source: 'hls' };
  }

  // Scale by duration, not by segment count: sampled segments aren't all the
  // playlist's average length (the last one in particular is usually short).
  const measuredDuration = measured.reduce((sum, item) => sum + (item.segment.durationSec || 0), 0);
  if (measuredDuration > 0 && durationSec > 0) {
    return { bytes: Math.round((measuredBytes / measuredDuration) * durationSec), isExact: false, source: 'hls' };
  }
  // No usable #EXTINF lengths to weight by — fall back to a per-segment average.
  return { bytes: Math.round((measuredBytes / measured.length) * segments.length), isExact: false, source: 'hls' };
};

// A valid video is never under 10 KB (e.g. 500 bytes is an XML error or m3u8 playlist file).
const MIN_VALID_VIDEO_BYTES = 10240;

export const getVideoSize = async (url, { downloadUrl } = {}) => {
  const candidates = [downloadUrl, url].filter(Boolean);

  for (const candidate of candidates) {
    if (isHlsUrl(candidate)) continue;
    const bytes = await measureFile(candidate);
    if (bytes && bytes >= MIN_VALID_VIDEO_BYTES) return { bytes, isExact: true, source: 'file' };
  }

  const hlsUrl = candidates.find(isHlsUrl);
  if (hlsUrl) {
    const res = await getHlsSize(hlsUrl);
    if (res?.bytes && res.bytes >= MIN_VALID_VIDEO_BYTES) return res;
  }
  return null;
};
export default getVideoSize;
